import React, { useState } from 'react';
import styled from '@emotion/native';
import { Linking, ScrollView, useWindowDimensions } from 'react-native';
import {
  lightGreyAlt,
  disabledLightGrey,
  green,
  lightGreen,
  mintGreenAlt,
  red,
} from '@/shared/styles/colors';
import { largeInputBorderStyle } from '@/shared/styles/styledComponents/editProfileForm';
import Button from '@/shared/components/Button';
import StaticInput from '@/shared/components/StaticInput';
import { BaseText } from '@/shared/components/StyledText';
import { IS_WEB } from '@/shared/constants/utility';

interface LookbookOfferProps {
  price: string;
  onPriceChange: (value: string) => void;
  note?: string;
  onNoteChange?: (value: string) => void;
  onSend: () => void;
  isDesktop: boolean;
  disabled?: boolean;
}

export const LookbookOffer: React.FC<LookbookOfferProps> = ({
  price,
  onPriceChange,
  note = '',
  onNoteChange,
  onSend,
  isDesktop,
  disabled,
}) => {
  const [showValidationError, setShowValidationError] = useState(false);
  const { height: windowHeight } = useWindowDimensions();
  const maxInputHeight = Math.min(windowHeight * 0.4, 260);

  const inputStyles = {
    height: 35,
    fontSize: 10,
    ...(isDesktop && { maxWidth: 118 }),
  };

  const noteInputStyles = {
    minHeight: isDesktop ? 35 : 28,
    fontSize: 10,
    borderRadius: 8,
    ...(isDesktop && { minWidth: 0 }),
  };

  const handleCanvaLinkPress = () => {
    const url = 'https://canva.com';
    if (IS_WEB) {
      window.open(url, '_blank');
    } else {
      Linking.openURL(url);
    }
  };

  // Validate price - minimum $50
  const priceValue = parseFloat(price) || 0;
  const isValidPrice = priceValue >= 50;
  const hasInvalidCharacters = price && !/^\d*\.?\d*$/.test(price);

  // Only show error if user tried to send and validation failed
  const showPriceError =
    showValidationError && (!isValidPrice || hasInvalidCharacters);

  const getErrorMessage = () => {
    if (hasInvalidCharacters) {
      return 'Please enter a valid price';
    }
    if (!isValidPrice) {
      return 'Minimum price is $50';
    }
    return '';
  };

  const handleSend = () => {
    // Check if price is valid before sending
    if (!price || !isValidPrice || hasInvalidCharacters) {
      setShowValidationError(true);
      return;
    }

    // Reset validation error state and proceed with send
    setShowValidationError(false);
    onSend();
  };

  // Reset validation error when user starts typing
  const handlePriceChange = (value: string) => {
    if (showValidationError) {
      setShowValidationError(false);
    }

    // Remove dollar sign if user types it, we'll add it in display
    const cleanValue = value.replace(/^\$/, '');
    onPriceChange(cleanValue);
  };

  // Display value with dollar sign when there's a price
  const displayValue = price ? `$${price}` : '';

  const content = (
    <>
      <OfferTextContainer>
        <OfferTitle>Backend Hours</OfferTitle>
        <OfferDescription>
          Additional paid time for your services to{' '}
          <LinkText onPress={handleCanvaLinkPress}>Create lookbooks,</LinkText>{' '}
          source pieces, or plan your client&apos;s wardrobe.
        </OfferDescription>
      </OfferTextContainer>
      <InputsRow isDesktop={isDesktop}>
        {onNoteChange && (
          <NoteInputContainer isDesktop={isDesktop}>
            <StaticInput
              placeholderText="Add a note for your client (optional)"
              labelText="NOTE (OPTIONAL)"
              value={note}
              customStyles={noteInputStyles}
              labelStyle={inputLabelStyles}
              contentStyle={{ ...inputContentStyles, borderRadius: 8 }}
              outlineStyle={[
                largeInputBorderStyle,
                { borderColor: mintGreenAlt },
              ]}
              multiline
              numberOfLines={isDesktop ? 2 : 1}
              onChange={(e) => onNoteChange?.(e.nativeEvent.text)}
              showLabel={true}
            />
          </NoteInputContainer>
        )}
        <PriceButtonRow isDesktop={isDesktop}>
          <PriceInputContainer isDesktop={isDesktop}>
            <StaticInput
              placeholderText="Enter price here"
              labelText="PRICE"
              value={displayValue}
              customStyles={inputStyles}
              labelStyle={inputLabelStyles}
              contentStyle={inputContentStyles}
              outlineStyle={{
                borderColor: showPriceError ? red : mintGreenAlt,
              }}
              keyboardType="numeric"
              onChange={(e) => handlePriceChange(e.nativeEvent.text)}
              showLabel={true}
            />
            {showPriceError && <ErrorText>{getErrorMessage()}</ErrorText>}
          </PriceInputContainer>
          <Button
            onPress={handleSend}
            text="SEND OFFER"
            disabled={disabled || !price}
            customStyle={buttonStyles}
            customLabelStyle={buttonLabelStyles}
          />
        </PriceButtonRow>
      </InputsRow>
    </>
  );

  return (
    <Container isDesktop={isDesktop}>
      {!isDesktop ? (
        <ScrollView
          showsVerticalScrollIndicator={false}
          style={{ maxHeight: maxInputHeight }}
          contentContainerStyle={{ flexGrow: 0, justifyContent: 'flex-start' }}
        >
          {content}
        </ScrollView>
      ) : (
        content
      )}
    </Container>
  );
};

const DESKTOP_CONTAINER_HORIZONTAL_PADDING = 24; // 12px each side

const Container = styled.View<{ isDesktop: boolean }>(({ isDesktop }) => ({
  flexDirection: 'column',
  alignItems: 'stretch',
  justifyContent: 'flex-start',
  backgroundColor: lightGreyAlt,
  borderWidth: 1,
  borderColor: disabledLightGrey,
  borderRadius: 8,
  width: '100%',
  maxWidth: '100%',
  ...(isDesktop
    ? {
        paddingVertical: 14,
        paddingHorizontal: 18,
        marginHorizontal: 0,
        ...(IS_WEB && {
          width:
            `calc(100% - ${DESKTOP_CONTAINER_HORIZONTAL_PADDING}px)` as unknown as number,
          alignSelf: 'center',
        }),
      }
    : {
        paddingTop: 16,
        paddingBottom: 16,
        paddingHorizontal: 16,
        marginHorizontal: 4,
      }),
  minHeight: isDesktop ? 130 : 0,
}));

const OfferTextContainer = styled.View({
  flexDirection: 'column',
  width: '100%',
  maxWidth: 400,
  marginBottom: 18,
});

const OfferTitle = styled(BaseText)({
  fontSize: 15,
  fontWeight: '700',
  color: green,
});

const OfferDescription = styled(BaseText)({
  fontSize: 12,
  color: lightGreen,
  marginTop: 4,
});

const LinkText = styled(BaseText)({
  fontSize: 12,
  color: green,
  fontWeight: '600',
  textDecorationLine: 'underline',
  cursor: 'pointer',
  lineHeight: 16,
});

const InputsRow = styled.View<{ isDesktop: boolean }>(({ isDesktop }) => ({
  flexDirection: isDesktop ? 'row' : 'column',
  alignItems: isDesktop ? 'center' : 'stretch',
  width: '100%',
  minWidth: 0,
  gap: isDesktop ? 16 : 10,
  ...(isDesktop &&
    IS_WEB && {
      display: 'grid' as unknown as 'flex',
      gridTemplateColumns: 'minmax(0, 1fr) auto',
    }),
}));

const PriceButtonRow = styled.View<{ isDesktop: boolean }>(({ isDesktop }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: isDesktop ? 16 : 10,
  ...(isDesktop ? { flex: 0, flexShrink: 0 } : { width: '100%' }),
}));

const NoteInputContainer = styled.View<{ isDesktop: boolean }>(
  ({ isDesktop }) => ({
    ...(isDesktop ? { flex: 1 } : { width: '100%', marginBottom: 8 }),
  }),
);

const PriceInputContainer = styled.View<{ isDesktop: boolean }>(
  ({ isDesktop }) => ({
    flexDirection: 'column',
    alignItems: 'flex-start',
    ...(isDesktop ? { flex: 0, minWidth: 130 } : { flex: 1, minWidth: 0 }),
  }),
);

const inputContentStyles = {
  fontSize: 10,
  paddingLeft: 4,
};

const inputLabelStyles = {
  fontSize: 7,
  left: 14,
};

const buttonStyles = {
  height: 40,
  width: 100,
};

const buttonLabelStyles = {
  fontSize: 8,
  letterSpacing: 0.41,
};

const ErrorText = styled(BaseText)({
  fontSize: 10,
  color: red,
  marginTop: 4,
  alignSelf: 'flex-start',
});
